/**
 * ViewBids.jsx — Buyer views, filters, and manages bids for a requirement.
 *
 * Filters: status, amount range, trust score range, sort order
 * Actions: shortlist, select winner
 *
 * Requirements: 23.3
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Rating,
  Tooltip,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterBar from '../../components/FilterBar';
import { apiClient } from '../../utils/auth';
import { buildProposalMediaUrl } from '../../utils/mediaUrls';
import DescriptionIcon from '@mui/icons-material/Description';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

const STATUS_COLORS = {
  SUBMITTED: 'default',
  SHORTLISTED: 'primary',
  WON: 'success',
  LOST: 'error',
};

const SORT_OPTIONS = [
  { value: 'newest',      label: 'Newest first' },
  { value: 'oldest',      label: 'Oldest first' },
  { value: 'amount_asc',  label: 'Amount: Low → High' },
  { value: 'amount_desc', label: 'Amount: High → Low' },
  { value: 'trust_desc',  label: 'Trust Score: High → Low' },
  { value: 'trust_asc',   label: 'Trust Score: Low → High' },
];

const STATUS_OPTIONS = [
  { value: 'SUBMITTED',   label: 'Submitted' },
  { value: 'SHORTLISTED', label: 'Shortlisted' },
  { value: 'WON',         label: 'Won' },
  { value: 'LOST',        label: 'Lost' },
];

const FILTER_CONFIG = [
  { key: 'sort',       type: 'select', label: 'Sort by',       options: SORT_OPTIONS },
  { key: 'status',     type: 'select', label: 'Bid Status',    options: STATUS_OPTIONS },
  { key: 'amount_min', type: 'number', label: 'Min Amount (Rs.)', min: 0, placeholder: 'e.g. 50000' },
  { key: 'amount_max', type: 'number', label: 'Max Amount (Rs.)', min: 0, placeholder: 'e.g. 500000' },
  {
    key: 'trust_range',
    type: 'range_slider',
    label: 'Trust Score Range',
    min: 0, max: 5, step: 0.5,
    format: (v) => `${v}★`,
  },
];

const DEFAULT_FILTERS = {
  sort: 'newest',
  status: '',
  amount_min: '',
  amount_max: '',
  trust_range: [0, 5],
};

function normalizeBidsPayload(data) {
  if (Array.isArray(data)) return { requirementStatus: 'OPEN', bids: data };
  if (data?.bids) return { requirementStatus: data.requirement_status || 'OPEN', bids: data.bids };
  return { requirementStatus: 'OPEN', bids: [] };
}

function TrustBadge({ score }) {
  if (score == null) return <Typography variant="caption" color="text.secondary">Trust: —</Typography>;
  return (
    <Tooltip title={`Trust score: ${score}/5`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
        <Typography variant="caption" fontWeight={600}>{Number(score).toFixed(1)}</Typography>
      </Box>
    </Tooltip>
  );
}

export default function ViewBids() {
  const { id: requirementId } = useParams();
  const navigate = useNavigate();

  const [allBids, setAllBids] = useState([]);
  const [requirementStatus, setRequirementStatus] = useState('OPEN');
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [finalizeBid, setFinalizeBid] = useState(null);

  const fetchBids = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all bids — filtering/sorting done client-side for trust score support
      const { data } = await apiClient.get(`/api/v1/requirements/${requirementId}/bids/`);
      const { requirementStatus: st, bids: list } = normalizeBidsPayload(data);
      setAllBids(list);
      setRequirementStatus(st);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      if (code === 'REQUIREMENT_FINALIZED') {
        setRequirementStatus('CLOSED');
        setAllBids([]);
      } else {
        setActionError('Failed to load bids.');
      }
    } finally {
      setLoading(false);
    }
  }, [requirementId]);

  useEffect(() => { fetchBids(); }, [fetchBids]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => setFilters(DEFAULT_FILTERS);

  // Client-side filter + sort
  const filteredBids = useMemo(() => {
    let bids = [...allBids];

    if (filters.status) bids = bids.filter((b) => b.bid_status === filters.status);
    if (filters.amount_min) bids = bids.filter((b) => Number(b.quoted_amount) >= Number(filters.amount_min));
    if (filters.amount_max) bids = bids.filter((b) => Number(b.quoted_amount) <= Number(filters.amount_max));

    const [tMin, tMax] = filters.trust_range || [0, 5];
    if (tMin > 0 || tMax < 5) {
      bids = bids.filter((b) => {
        const ts = b.bidder_trust_score;
        if (ts == null) return tMin === 0; // include null scores only if min is 0
        return ts >= tMin && ts <= tMax;
      });
    }

    // Sort
    switch (filters.sort) {
      case 'amount_asc':  bids.sort((a, b) => Number(a.quoted_amount) - Number(b.quoted_amount)); break;
      case 'amount_desc': bids.sort((a, b) => Number(b.quoted_amount) - Number(a.quoted_amount)); break;
      case 'trust_desc':  bids.sort((a, b) => (b.bidder_trust_score ?? 0) - (a.bidder_trust_score ?? 0)); break;
      case 'trust_asc':   bids.sort((a, b) => (a.bidder_trust_score ?? 0) - (b.bidder_trust_score ?? 0)); break;
      case 'oldest':      bids.sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at)); break;
      default:            bids.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
    }

    return bids;
  }, [allBids, filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.sort !== 'newest') count++;
    if (filters.status) count++;
    if (filters.amount_min) count++;
    if (filters.amount_max) count++;
    const [tMin, tMax] = filters.trust_range || [0, 5];
    if (tMin > 0 || tMax < 5) count++;
    return count;
  }, [filters]);

  const handleShortlist = async (bidId) => {
    setActionLoading('shortlist-' + bidId);
    setActionError('');
    try {
      await apiClient.post(`/api/v1/bids/${bidId}/shortlist/`);
      await fetchBids();
    } catch (err) {
      setActionError(err.response?.data?.error?.message || 'Failed to shortlist bid.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSelectWinner = (bid) => {
    setFinalizeBid(bid);
    setActionError('');
  };

  const confirmSelectWinner = async () => {
    if (!finalizeBid) return;
    const bidId = finalizeBid.id;
    setActionLoading('select-' + bidId);
    setActionError('');
    try {
      const { data } = await apiClient.post(`/api/v1/bids/${bidId}/select/`);
      setFinalizeBid(null);
      const dealId = data?.deal?.deal_id;
      navigate(dealId ? `/deals/${dealId}` : '/deals/mine', { replace: true });
    } catch (err) {
      const code = err.response?.data?.error?.code;
      setActionError(
        code === 'WINNING_BID_EXISTS'
          ? 'A winning bid has already been selected.'
          : code === 'DEAL_CREATION_FAILED'
          ? (err.response?.data?.error?.message || 'Bid could not be finalized because deal creation failed.')
          : err.response?.data?.error?.message || 'Failed to select winner.'
      );
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const isClosed = requirementStatus === 'CLOSED';

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: 4, p: 3 }}>
      {/* Dedicated Back Button */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/buyer/requirements')}
        sx={{
          mb: 3,
          borderColor: 'rgba(99, 102, 241, 0.4)',
          color: '#818cf8',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
          }
        }}
      >
        Back to Requirements
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4">Bids for Requirement #{requirementId}</Typography>
        <Chip label={requirementStatus} size="small" color={isClosed ? 'warning' : 'default'} />
      </Box>

      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}

      {isClosed && allBids.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          This requirement is closed. Use <strong>My deals</strong> to continue.
          <Button variant="contained" size="small" sx={{ ml: 2 }} onClick={() => navigate('/deals/mine')}>My deals</Button>
        </Alert>
      ) : (
        <>
          <FilterBar
            config={FILTER_CONFIG}
            values={filters}
            onChange={handleFilterChange}
            onReset={handleReset}
            activeCount={activeFilterCount}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredBids.length} of {allBids.length} bid{allBids.length !== 1 ? 's' : ''}
          </Typography>
        </>
      )}

      {!isClosed && filteredBids.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.01)', borderStyle: 'dashed', borderWidth: '1.5px', borderColor: 'rgba(255, 255, 255, 0.12)', borderRadius: '16px', mt: 3 }}>
          <Box sx={{ color: 'primary.light', mb: 2.5, display: 'flex', justifyContent: 'center' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>No Bids Received Yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mx: 'auto', lineHeight: 1.65 }}>
            Project bids submitted anonymously by pre-verified cybersecurity professionals will automatically appear here. We will alert you the moment a new bid arrives!
          </Typography>
        </Paper>
      ) : !isClosed ? (
        filteredBids.map((bid) => (
          <Paper key={bid.id} sx={{ p: 2, mb: 2 }} elevation={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Bidder ID:{' '}
                <span
                  style={{ color: '#818cf8', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                  onClick={() => navigate(`/buyer/bidders/${bid.bidder_anonymous_id}?reqId=${requirementId}&bidId=${bid.id}`)}
                >
                  {bid.bidder_anonymous_id}
                </span>
              </Typography>
              <Chip
                label={bid.bid_status}
                color={STATUS_COLORS[bid.bid_status] || 'default'}
                size="small"
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Typography variant="body1">
                <strong>Amount:</strong> Rs.{Number(bid.quoted_amount).toLocaleString('en-IN')}
              </Typography>
              <TrustBadge score={bid.bidder_trust_score} />
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Submitted: {new Date(bid.submitted_at).toLocaleDateString()}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>Delivery Plan:</strong> {bid.delivery_plan}
            </Typography>

            {bid.proposal_doc_s3_key && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DescriptionIcon />}
                href={buildProposalMediaUrl(bid.proposal_doc_s3_key)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  borderColor: 'rgba(16, 185, 129, 0.4)',
                  color: '#34d399',
                  '&:hover': {
                    borderColor: '#10b981',
                    bgcolor: 'rgba(16, 185, 129, 0.08)',
                  },
                }}
              >
                View Delivery Document
              </Button>
            )}

            {bid.deal_id && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Deal #{bid.deal_id}
              </Typography>
            )}

            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`/buyer/bidders/${bid.bidder_anonymous_id}?reqId=${requirementId}&bidId=${bid.id}`)}
                sx={{
                  textTransform: 'none',
                  borderColor: 'rgba(129, 140, 248, 0.4)',
                  color: '#a5b4fc',
                  '&:hover': {
                    borderColor: '#818cf8',
                    bgcolor: 'rgba(129, 140, 248, 0.08)',
                  }
                }}
              >
                View Profile
              </Button>

              {!isClosed && bid.bid_status === 'SUBMITTED' && (
                <>
                  <Button variant="outlined" size="small" onClick={() => handleShortlist(bid.id)} disabled={actionLoading === 'shortlist-' + bid.id} aria-label={`Shortlist bid ${bid.id}`}>
                    {actionLoading === 'shortlist-' + bid.id ? <CircularProgress size={16} /> : 'Shortlist'}
                  </Button>
                  <Button variant="contained" size="small" color="success" onClick={() => handleSelectWinner(bid)} disabled={actionLoading === 'select-' + bid.id} aria-label={`Select bid ${bid.id} as winner`}>
                    {actionLoading === 'select-' + bid.id ? <CircularProgress size={16} /> : 'Select Winner'}
                  </Button>
                </>
              )}

              {!isClosed && bid.bid_status === 'SHORTLISTED' && (
                <Button variant="contained" size="small" color="success" onClick={() => handleSelectWinner(bid)} disabled={actionLoading === 'select-' + bid.id} aria-label={`Select shortlisted bid ${bid.id} as winner`}>
                  {actionLoading === 'select-' + bid.id ? <CircularProgress size={16} /> : 'Select Winner'}
                </Button>
              )}
            </Box>
          </Paper>
        ))
      ) : null}

      <Dialog
        open={Boolean(finalizeBid)}
        onClose={() => setFinalizeBid(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Finalize this bidder?</DialogTitle>
        <DialogContent>
          {finalizeBid && (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Review the delivery plan and proposal document before confirming. This action closes the requirement and creates a deal.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Quoted amount:</strong> Rs.{Number(finalizeBid.quoted_amount).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <strong>Delivery plan:</strong> {finalizeBid.delivery_plan}
              </Typography>
              {finalizeBid.proposal_doc_s3_key ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DescriptionIcon />}
                  href={buildProposalMediaUrl(finalizeBid.proposal_doc_s3_key)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: 'none', mb: 1 }}
                >
                  Open Delivery Document
                </Button>
              ) : (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  No delivery document was attached to this bid.
                </Alert>
              )}
            </>
          )}
          {actionError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setActionError('')}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizeBid(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={actionLoading === 'select-' + finalizeBid?.id}
            onClick={confirmSelectWinner}
          >
            {actionLoading === 'select-' + finalizeBid?.id ? <CircularProgress size={18} /> : 'Confirm & Finalize'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
